import { of, EMPTY, fromEvent, merge, combineLatest } from 'rxjs';
import {
    switchMap,
    catchError,
    tap,
    debounceTime,
    startWith,
    map,
} from 'rxjs/operators';
import { fetchJson } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';
import { populateSubjectSelect } from './subjects.js';
import { populateSchoolGradeSelect } from './school-grades.js';

/**
 * Renders the card list into the given <ul> element from an array of card
 * objects. Each item shows the front text, subject name, class info, and a
 * link to the edit view.
 */
function renderCardList(listEl, cards) {
    listEl.innerHTML = '';
    if (!cards || cards.length === 0) {
        let empty = document.createElement('li');
        empty.className = 'cards-list-empty';
        empty.textContent = 'Keine Karten gefunden.';
        listEl.appendChild(empty);
        return;
    }
    for (let card of cards) {
        let li = document.createElement('li');
        li.className = 'cards-list-item';

        let frontEl = document.createElement('span');
        frontEl.className = 'cards-list-front';
        frontEl.textContent = card.front;

        let metaContainer = document.createElement('div');
        metaContainer.className = 'cards-meta-container';

        let metaEl = document.createElement('span');
        metaEl.className = 'cards-list-meta';
        let parts = [];
        if (card.subject_name) {
            parts.push(card.subject_name);
        }
        if (card.grundschule_class != null) {
            parts.push(`GS ${card.grundschule_class}. Kl.`);
        }
        if (card.gymnasium_class != null) {
            parts.push(`Gym ${card.gymnasium_class}. Kl.`);
        }
        metaEl.textContent = parts.length > 0 ? parts.join(' · ') : '–';

        let editLink = document.createElement('a');
        editLink.href = `#edit/${card.id}`;
        editLink.className = 'cards-list-edit';
        editLink.textContent = 'Bearbeiten';

        metaContainer.appendChild(metaEl);
        metaContainer.appendChild(editLink);

        li.appendChild(frontEl);
        li.appendChild(metaContainer);
        listEl.appendChild(li);
    }
}

/**
 * Builds the query string from the current filter values. Only includes
 * non-empty values.
 */
function buildQuery(grundschuleClass, gymnasiumClass, subjectId, topicId) {
    let params = new URLSearchParams();
    if (grundschuleClass) {
        params.set('grundschule_class', grundschuleClass);
    }
    if (gymnasiumClass) {
        params.set('gymnasium_class', gymnasiumClass);
    }
    if (subjectId) {
        params.set('subject_id', subjectId);
    }
    if (topicId) {
        params.set('topic_id', topicId);
    }
    let qs = params.toString();
    return qs ? `/api/cards?${qs}` : '/api/cards';
}

/**
 * Populates the topic <select> with all topics from GET /api/topics. Returns
 * an Observable that completes after populating.
 */
function populateTopicSelect(selectEl) {
    return fetchJson('/api/topics').pipe(
        tap((topics) => {
            for (let topic of topics) {
                let option = document.createElement('option');
                option.value = String(topic.id);
                option.textContent = topic.name;
                selectEl.appendChild(option);
            }
        }),
        catchError((err) => {
            showError(err);
            return EMPTY;
        })
    );
}

/**
 * Creates the cards list view. Renders a filter panel (Grundschule class,
 * Gymnasium class, subject, topic) and a card list that updates automatically
 * as filters change. Up to 50 cards are shown, sorted by last modified date
 * descending.
 *
 * An optional initialFilters object may be passed to pre-select filter values
 * on mount (e.g. when navigating from the topics view via #cards?topic_id=3).
 * Recognised keys: topicId, subjectId, grundschuleClass, gymnasiumClass.
 */
export function createCardsView(initialFilters = {}) {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('cards-view');
            replaceViewContainer(el);

            let grundschuleSelect = el.querySelector(
                '.grundschule-class-filter'
            );
            let gymnasiumSelect = el.querySelector('.gymnasium-class-filter');
            let subjectSelect = el.querySelector('.subject-filter');
            let topicSelect = el.querySelector('.topic-filter');
            let listEl = el.querySelector('.cards-list');

            populateSchoolGradeSelect(grundschuleSelect, 'grundschule');
            populateSchoolGradeSelect(gymnasiumSelect, 'gymnasium');

            if (initialFilters.grundschuleClass) {
                grundschuleSelect.value = initialFilters.grundschuleClass;
            }
            if (initialFilters.gymnasiumClass) {
                gymnasiumSelect.value = initialFilters.gymnasiumClass;
            }

            let grundschule$ = fromEvent(grundschuleSelect, 'change').pipe(
                map((e) => e.target.value),
                startWith(
                    initialFilters.grundschuleClass || grundschuleSelect.value
                )
            );
            let gymnasium$ = fromEvent(gymnasiumSelect, 'change').pipe(
                map((e) => e.target.value),
                startWith(
                    initialFilters.gymnasiumClass || gymnasiumSelect.value
                )
            );
            let subject$ = fromEvent(subjectSelect, 'change').pipe(
                map((e) => e.target.value),
                startWith(initialFilters.subjectId || subjectSelect.value)
            );
            let topic$ = fromEvent(topicSelect, 'change').pipe(
                map((e) => e.target.value),
                startWith(initialFilters.topicId || topicSelect.value)
            );

            let list$ = combineLatest([
                grundschule$,
                gymnasium$,
                subject$,
                topic$,
            ]).pipe(
                debounceTime(200),
                switchMap(
                    ([
                        grundschuleClass,
                        gymnasiumClass,
                        subjectId,
                        topicId,
                    ]) => {
                        let url = buildQuery(
                            grundschuleClass,
                            gymnasiumClass,
                            subjectId,
                            topicId
                        );
                        return fetchJson(url).pipe(
                            tap((cards) => renderCardList(listEl, cards)),
                            catchError((err) => {
                                showError(err);
                                return EMPTY;
                            })
                        );
                    }
                )
            );

            let subjects$ = populateSubjectSelect(subjectSelect).pipe(
                tap(() => {
                    if (initialFilters.subjectId) {
                        subjectSelect.value = initialFilters.subjectId;
                    }
                })
            );
            let topics$ = populateTopicSelect(topicSelect).pipe(
                tap(() => {
                    if (initialFilters.topicId) {
                        topicSelect.value = initialFilters.topicId;
                    }
                })
            );

            return merge(list$, subjects$, topics$);
        }),
        catchError((err) => {
            showError(err);
            return EMPTY;
        })
    );
}
